import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Grid, Stack, Button, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { AppColors } from '../../Utils/helper/AppColors';
import PageHeading from '../../Components/PageHeading';
import MetricCard from './Components/MatricCard';
import { ArrowTrendUpIcon, CreditCardIcon, GraphIcon } from '../../Components/Icons/Icons';
import { theme } from 'antd';
import CustomTag from '../../Components/CustomTag';
import CultService from '../../Services/CultService';
import moment from 'moment';
import { useSelector } from 'react-redux';
import DataNotFound from '../../Components/DataNotFound';
import toast from 'react-hot-toast';
import AnalyticsCard from './Components/AnalyticsCard';

export default function Subscription() {
	const [activeView, setActiveView] = useState(0); // Changed to index instead of string
	const [subscriptionData, setSubscriptionData] = useState(null);
	const [subscriptionUsageAnalyticsData, setSubscriptionUsageAnalyticsData] = useState(null);
	const [creditDashboardInsightsData, setCreditDashboardInsightsData] = useState(null);
	const [loading, setLoading] = useState(true);
	const company = useSelector((state) => state?.app?.company);

	const formatValidityDate = (dateString) => {
		if (!dateString) return '--';
		const date = moment(dateString);
		return date.format('DD');
	};

	const formatValidityMonth = (dateString) => {
		if (!dateString) return '--';
		const date = moment(dateString);
		return date.format('MMMM');
	};

	const formatValidityYear = (dateString) => {
		if (!dateString) return '--';
		const date = moment(dateString);
		return date.format('YYYY');
	};

	const formatDateToMMDDYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}-${day}-${year}`;
};


	// Updated to match your tab structure
	const viewTabs = [
		{ code: 'Daily View', type: 'daily' },
		{ code: 'Weekly View', type: 'weekly' },
		{ code: 'Monthly View', type: 'monthly' },
	];

	const handleViewChange = (event, newValue) => {
		if (newValue !== null) {
			setActiveView(newValue);
			fetchSubscriptionUsageAnalyticsData(viewTabs[newValue].type);
		}
	};

	const creditCards = [
		{
			title: 'Available Credits',
			value: subscriptionData?.availableCredits || '0',
			subtitle: 'Ready to use',
			background: 'linear-gradient(90.18deg, #DBEAFE 0.15%, #EFF6FF 99.85%)',
			titleColor: '#1D4ED8',
			valueColor: '#1E40AF',
			subtitleColor: '#1D4ED8',
			borderColor: '#BFDBFE',
		},
		{
			title: 'Total Credits',
			value: subscriptionData?.totalCredits || '0',
			subtitle: 'Total allocated',
			background: 'linear-gradient(90.18deg, #F3E8FF 0.15%, #FAF5FF 99.85%)',
			titleColor: '#7E22CE',
			valueColor: '#6B21A8',
			subtitleColor: '#7E22CE',
			borderColor: '#E9D5FF',
		},
		{
			title: 'Used Credits',
			value: subscriptionData?.usedCredits || '0',
			subtitle: 'Credits consumed',
			background: 'linear-gradient(90.18deg, #FFEDD5 0.15%, #FFF7ED 99.85%)',
			titleColor: '#C2410C',
			valueColor: '#9A3412',
			subtitleColor: '#C2410C',
			borderColor: '#FED7AA',
		},
		{
			title: 'Validity',
			// value: formatValidityDate(subscriptionData?.validity) || 'N/A',
			// subtitle: formatValidityMonth(subscriptionData?.validity) || 'N/A',
			// extra: formatValidityYear(subscriptionData?.validity) || 'N/A',
			extra : true,
			value: formatDateToMMDDYYYY(subscriptionData?.validity) || 'N/A',
			background: 'linear-gradient(90.18deg, #DCFCE7 0.15%, #F0FDF4 99.85%)',
			titleColor: '#15803D',
			valueColor: '#0B832F',
			subtitleColor: '#15803D',
			borderColor: '#84F6AC',
		},
	];

	const usageInsightsAll = [
		{
			label: 'Peak Usage Day',
			value:  moment(creditDashboardInsightsData?.usageInsights?.peakUsageDay, 'YYYY-MM-DD').format('MM-DD-YYYY') || creditDashboardInsightsData?.usageInsights?.peakUsageDay || 'N/A',
			isChip: true,
			chipColor: { color: '#fff', bgColor: '#1D4ED8', border: '#1D4ED8' },
		},
		{
			label: 'Avg. Daily Usage',
			value: creditDashboardInsightsData?.usageInsights?.avgDailyUsage || 'N/A',
			isChip: true,
			chipColor: { color: '#545460', bgColor: '#F1F5F9', border: '#F1F5F9' },
		},
		{
			label: 'Growth Trend',
			value: creditDashboardInsightsData?.usageInsights?.growthTrend || '0',
			isChip: true,
			chipColor: { color: '#fff', bgColor: '#16A34A', border: '#16A34A' },
		},
	];

	// Function to fetch subscription data
	const fetchSubscriptionData = async () => {
		try {
			setLoading(true);

			const response = await CultService.getSubscription({
				params: { companyId: company?._id },
			});

			if (response?.data?.success) {
				setSubscriptionData(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching subscription data:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchSubscriptionUsageAnalyticsData = async (view = 'daily') => {
		try {
			setLoading(true);
			const response = await CultService.getSubscriptionUsageAnalytics({ params: { companyId: company?._id, view: view } });
			if (response?.data?.success) {
				setSubscriptionUsageAnalyticsData(response.data.data);
			}
		} catch (error) {
			toast.error('Error fetching subscription usage analytics data');
			console.error('Error fetching subscription usage analytics data:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchCreditDashboardInsightsData = async () => {
		try {
			setLoading(true);
			const response = await CultService.getCreditDashboardInsights({
				params: { companyId: company?._id },
			});
			if (response?.data?.success) {
				setCreditDashboardInsightsData(response.data.data);
			}
		} catch (error) {
			toast.error('Error fetching credit dashboard insights data');
			console.error('Error fetching credit dashboard insights data:', error);
		} finally {
			setLoading(false);
		}
	};
	// Fetch data on component mount
	useEffect(() => {
		if (company?._id) {
			fetchSubscriptionData();
			fetchSubscriptionUsageAnalyticsData();
			fetchCreditDashboardInsightsData();
		}
	}, [company?._id]);

	return (
		<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
			<PageHeading
				title={`Subscription Module`}
				titleSx={{
					fontSize: 24,
					fontWeight: 600,
					lineHeight: '130%',
				}}
				subTitle="Easily Manage Your Subscription Plan, Billing, and Preferences"
			/>

			{/* Credit Availability Cards */}
			<Box>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 40,
							height: 40,
							borderRadius: '4px',
							backgroundColor: '#1E40AF',
						}}
					>
						<CreditCardIcon color={'#fff'} size={22} />
					</Box>
					<Typography
						sx={{
							fontWeight: 600,
							color: '#212126', // AppColors.HEADING_TITLE
							fontSize: 20,
							lineHeight: '130%',
							mb: 3,
						}}
					>
						Credit Availability
					</Typography>
				</Stack>

				<Grid container spacing={3}>
					{creditCards.map((card, index) => (
						<Grid item xs={12} sm={6} md={3} key={index}>
							<MetricCard
								title={card.title}
								value={card.value}
								extra={card.extra}
								subtitle={card.subtitle}
								background={card.background}
								titleColor={card.titleColor}
								valueColor={card.valueColor}
								subtitleColor={card.subtitleColor}
								borderColor={card.borderColor}
							/>
						</Grid>
					))}
				</Grid>
			</Box>

			{/* Consumption Reports */}
			<Box sx={{}}>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 40,
							height: 40,
							borderRadius: '4px',
							backgroundColor: '#9333EA',
						}}
					>
						<GraphIcon color={'#fff'} size={22} />
					</Box>
					<Typography
						sx={{
							fontWeight: 600,
							color: '#212126', // AppColors.HEADING_TITLE
							fontSize: 20,
							lineHeight: '130%',
							mb: 3,
						}}
					>
						Consumption Reports
					</Typography>
				</Stack>
				<Card sx={{ p: 4, borderRadius: '15px', border: `1px solid #D8CFF9` }}>
					<Typography
						sx={(theme) => ({
							fontSize: 20,
							fontWeight: 600,
							color: theme.palette.primary.darker_primary || '#683FC7',
							fontFamily: 'Poppins',
						})}
					>
						Usage Analytics
					</Typography>
					<Typography
						sx={{
							fontSize: 14,
							fontWeight: 400,
							color: AppColors.HEADING_TITLE2,
							fontFamily: 'Poppins',
							mb: 2,
						}}
					>
						Detailed breakdown of your credit consumption patterns
					</Typography>

					{/* Replace the Button Stack with ToggleButtonGroup */}
					<ToggleButtonGroup size="small" sx={{ width: '100%', mb: 4 }} value={activeView} exclusive onChange={handleViewChange}>
						{viewTabs?.map((tab, index) => (
							<ToggleButton
								key={index}
								sx={(theme) => ({
									textTransform: 'none',
									borderRadius: '10px',
									border: '1px solid',
									borderColor: theme.palette.secondary.bg_gray || theme.palette.primary.lighter_shade || '#e0e0e0',
									alignItems: 'center',
									flex: 1,
								    backgroundColor: theme.palette.secondary.lightest_grey || AppColors.NEW_BACKGROUND4,
									fontFamily: 'Poppins',
									'&.Mui-selected': {
										backgroundColor: theme.palette.secondary.orange || theme.palette.primary.lightest_shade,
										color: theme.palette.secondary.orange || theme.palette.primary.darker_primary,
									},
								})}
								value={index}
							>
								<Typography
									sx={(theme) => (
										activeView === index ? { color: theme.palette.primary.white } : { color: theme.palette.secondary.orange }
									)}
									fontSize={14}
									fontWeight={600}
									lineHeight={'150%'}
								>
									{tab?.code}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>

					{/* Consumption Data Cards */}
					<Box
						sx={{
							width: '100%',
							// maxWidth: 'calc(4 * 290px + 3 * 16px)',
							overflowX: 'auto',
							pb: 2,
							'&::-webkit-scrollbar': {
								height: 8,
							},
							'&::-webkit-scrollbar-track': {
								backgroundColor: '#f1f1f1',
								borderRadius: 4,
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: '#c1c1c1',
								borderRadius: 4,
							},
							'&::-webkit-scrollbar-thumb:hover': {
								backgroundColor: '#a8a8a8',
							},
						}}
					>
						<Stack
							direction="row"
							spacing={2}
							// sx={{
							// 	width: 'max-content',

							// }}
						>
							{!subscriptionUsageAnalyticsData || subscriptionUsageAnalyticsData?.length !== 0 ? (
								subscriptionUsageAnalyticsData?.map((item, index) => (
									<AnalyticsCard 
									heading={ activeView === 0 ? moment(item?.date).format('MMM DD') : activeView === 1 ? item?.date : moment(item?.date).format('MMMM')} 
									creadit={item?.creditsUsed}
									label={activeView === 0 ? item?.label : activeView === 1 ? item?.label : moment(item?.date).format('MMM YYYY')}
									tagTitle="Credits Used" 
									/>
								))
							) : (
								<Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' , borderRadius: '8px', p: 2 }}>
								<DataNotFound />
								</Stack>
							)}
						</Stack>
					</Box>
				</Card>
			</Box>

			{/* Predictive Analysis */}
			<Box>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 40,
							height: 40,
							borderRadius: '4px',
							backgroundColor: '#16A34A',
						}}
					>
						<ArrowTrendUpIcon color={'#fff'} size={22} />
					</Box>
					<Typography
						sx={{
							fontWeight: 600,
							color: '#212126', // AppColors.HEADING_TITLE
							fontSize: 20,
							lineHeight: '130%',
							mb: 3,
						}}
					>
						Predictive Analysis
					</Typography>
				</Stack>

				<Grid container spacing={3}>
					{/* Prediction Card */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								background: 'linear-gradient(90.18deg, #DCFCE7 0.15%, #F0FDF4 99.85%)',
								p: 3,
								borderRadius: 2,
								border: `1px solid #BBF7D0`,
								height: '100%', // This makes both cards same height
							}}
						>
							<Typography
								sx={{
									fontSize: 20,
									fontWeight: 600,
									color: '#166534',
									mb: 1,
									alignItems: 'center',
									display: 'flex',
									gap: 1,
								}}
							>
								<ArrowTrendUpIcon color={'#166534'} size={22} />{' '}
								{moment(creditDashboardInsightsData?.prediction?.month).format('MMMM YYYY')} Prediction
							</Typography>

							<Typography
								sx={{
									fontSize: 14,
									fontWeight: 400,
									color: '#16A34A',
									mb: 3,
								}}
							>
								Based on your current usage patterns
							</Typography>
							<Stack direction={'row'} sx={{ alignItems: 'center', justifyContent: 'space-around', width: '100%', mt: '25px' }}>
								<Stack direction="column" sx={{ alignItems: 'center' }}>
									<Typography
										sx={{
											fontSize: 36,
											fontWeight: 600,
											color: '#166534',
											fontFamily: 'Poppins',
											mr: 1,
										}}
									>
										~{creditDashboardInsightsData?.prediction?.predictedCredits}
									</Typography>
									<Typography
										sx={{
											fontSize: 14,
											fontWeight: 400,
											color: '#16A34A',
											fontFamily: 'Poppins',
										}}
									>
										Predicted credits needed
									</Typography>
								</Stack>

								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
										border: '1px solid #BBF7D0',
										borderRadius: 1,
										p: '12px 25px',
										bgcolor: '#fff',
									}}
								>
									<Typography
										sx={{
											fontSize: 12,
											fontWeight: 600,
											color: '#166534',
											fontFamily: 'Poppins',
											mr: 1,
										}}
									>
										{creditDashboardInsightsData?.prediction?.growthPercent}%
									</Typography>
									<Typography
										sx={{
											fontSize: 10,
											fontWeight: 400,
											color: '#16A34A',
											fontFamily: 'Poppins',
										}}
									>
										vs last month
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>

					{/* Usage Insights Card */}
					<Grid item xs={12} md={6}>
						<Card
							sx={{
								background: 'linear-gradient(90.18deg, #DBEAFE 0.15%, #EFF6FF 99.85%)',
								p: 4,
								borderRadius: 2,
								height: '100%', // This makes both cards same height
							}}
						>
							<Typography
								sx={{
									fontSize: 20,
									fontWeight: 600,
									color: '#1D4ED8',
								}}
							>
								Usage Insights
							</Typography>
							<Typography
								sx={{
									fontSize: 14,
									fontWeight: 400,
									color: '#1D4ED8',
									mb: 2,
								}}
							>
								Key patterns from your consumption data
							</Typography>
							<Stack spacing={1} sx={{ width: '100%' }}>
								{usageInsightsAll.map((insight, index) => (
									<Box
										key={index}
										sx={{
											bgcolor: 'white',
											p: 1,
											borderRadius: 1,
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											width: '100%',
										}}
									>
										<Typography
											sx={{
												fontSize: 14,
												fontWeight: 500,
												color: '#1D4ED8',
											}}
										>
											{insight.label}
										</Typography>
										<CustomTag tagTitle={insight.value} colorsInfo={insight.chipColor} />
									</Box>
								))}
							</Stack>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}
